import { supabase } from './supabase';
import type { PriceListItem } from './supabase';

const mockPriceList: PriceListItem[] = [
  {
    id: '1',
    service_type: 'lab',
    service_name: 'Complete Blood Count (CBC)',
    category: 'Hematology',
    price: 500,
    discount_allowed: true,
    effective_from: new Date().toISOString().split('T')[0],
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '2',
    service_type: 'lab',
    service_name: 'Blood Sugar (Fasting)',
    category: 'Biochemistry',
    price: 300,
    discount_allowed: true,
    effective_from: new Date().toISOString().split('T')[0],
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '3',
    service_type: 'radiology',
    service_name: 'X-Ray Chest PA',
    category: 'X-Ray',
    price: 800,
    discount_allowed: true,
    effective_from: new Date().toISOString().split('T')[0],
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '4',
    service_type: 'opd',
    service_name: 'General Consultation',
    category: 'Consultation',
    price: 1000,
    discount_allowed: false,
    effective_from: new Date().toISOString().split('T')[0],
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const isSupabaseConfigured = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  return url && url !== 'https://placeholder.supabase.co';
};

export const pricingService = {
  async getAll(filters?: {
    service_type?: string;
    category?: string;
    search?: string;
  }): Promise<PriceListItem[]> {
    if (!isSupabaseConfigured()) {
      let filtered = [...mockPriceList];
      
      if (filters?.service_type) {
        filtered = filtered.filter(p => p.service_type === filters.service_type);
      }
      if (filters?.category) {
        filtered = filtered.filter(p => p.category === filters.category);
      }
      if (filters?.search) {
        const search = filters.search.toLowerCase();
        filtered = filtered.filter(p => 
          p.service_name.toLowerCase().includes(search) ||
          p.service_code?.toLowerCase().includes(search)
        );
      }
      
      return filtered;
    }

    try {
      let query = supabase
        .from('price_list')
        .select('*')
        .eq('status', 'active');

      if (filters?.service_type) {
        query = query.eq('service_type', filters.service_type);
      }
      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.search) {
        query = query.or(`service_name.ilike.%${filters.search}%,service_code.ilike.%${filters.search}%`);
      }

      query = query.order('service_name', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching price list:', error);
      return mockPriceList;
    }
  },

  async getCategories(serviceType?: string): Promise<string[]> {
    if (!isSupabaseConfigured()) {
      let filtered = mockPriceList;
      if (serviceType) {
        filtered = filtered.filter(p => p.service_type === serviceType);
      }
      const categories = [...new Set(filtered.map(p => p.category).filter(Boolean))];
      return categories as string[];
    }

    try {
      let query = supabase
        .from('price_list')
        .select('category')
        .eq('status', 'active')
        .not('category', 'is', null);

      if (serviceType) {
        query = query.eq('service_type', serviceType);
      }

      const { data, error } = await query;

      if (error) throw error;
      const categories = [...new Set(data?.map(d => d.category).filter(Boolean))];
      return categories as string[];
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  async getById(id: string): Promise<PriceListItem | null> {
    if (!isSupabaseConfigured()) {
      return mockPriceList.find(p => p.id === id) || null;
    }

    try {
      const { data, error } = await supabase
        .from('price_list')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching price item:', error);
      return mockPriceList.find(p => p.id === id) || null;
    }
  },

  async create(item: Omit<PriceListItem, 'id' | 'created_at' | 'updated_at'>): Promise<PriceListItem> {
    if (!isSupabaseConfigured()) {
      const newItem: PriceListItem = {
        ...item,
        id: Math.random().toString(36).substr(2, 9),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      mockPriceList.unshift(newItem);
      return newItem;
    }

    try {
      const { data, error } = await supabase
        .from('price_list')
        .insert([item])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating price item:', error);
      throw error;
    }
  },

  async update(id: string, updates: Partial<PriceListItem>): Promise<PriceListItem> {
    if (!isSupabaseConfigured()) {
      const index = mockPriceList.findIndex(p => p.id === id);
      if (index !== -1) {
        mockPriceList[index] = {
          ...mockPriceList[index],
          ...updates,
          updated_at: new Date().toISOString(),
        };
        return mockPriceList[index];
      }
      throw new Error('Price item not found');
    }

    try {
      const { data, error } = await supabase
        .from('price_list')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating price item:', error);
      throw error;
    }
  },

  async bulkUpdate(updates: Array<{ id: string; price: number; discount_allowed?: boolean }>): Promise<void> {
    if (!isSupabaseConfigured()) {
      updates.forEach(update => {
        const index = mockPriceList.findIndex(p => p.id === update.id);
        if (index !== -1) {
          mockPriceList[index] = {
            ...mockPriceList[index],
            price: update.price,
            discount_allowed: update.discount_allowed ?? mockPriceList[index].discount_allowed,
            updated_at: new Date().toISOString(),
          };
        }
      });
      return;
    }

    try {
      const promises = updates.map(update =>
        supabase
          .from('price_list')
          .update({
            price: update.price,
            ...(update.discount_allowed !== undefined && { discount_allowed: update.discount_allowed }),
          })
          .eq('id', update.id)
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Error bulk updating prices:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      const index = mockPriceList.findIndex(p => p.id === id);
      if (index !== -1) {
        mockPriceList.splice(index, 1);
      }
      return;
    }

    try {
      const { error } = await supabase
        .from('price_list')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting price item:', error);
      throw error;
    }
  },
};
